import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Container, Row } from "reactstrap";
import { useNavigate } from "react-router-dom";

//Import Components
import TopRewardsWidget from "./mini-widget";
import ExplorePartnersSection from "./ExplorePartnersSection";
import ConnectAccountModal from "../../components/Common/ConnectAccountModal";
import RedeemVoucherModal from "../../components/Common/RedeemVoucherModal";
import SecondaryButton from "../../components/Common/SecondaryButton";
import CardConnectBanner from "../../components/Common/cardBanner";

//Import API
import endpoints from "../../api/endpoints";
import { get } from "../../api/axios";

//Utils
import { extractErrorMessage } from "../../utils/errorHandler";
import fallbackImage from "../../assets/images/user-dashboard/image.svg";

//Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

const Dashboard = () => {
  document.title = "Dashboard | Omaha Food Lovers";

  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalRestaurants, setTotalRestaurants] = useState(0);
  const [showLoadMoreButton, setShowLoadMoreButton] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [visitedFilter, setVisitedFilter] = useState("all"); // all, visited, not_visited
  const [categories, setCategories] = useState([]);
  const [categoriesCurrentPage, setCategoriesCurrentPage] = useState(1);
  const [categoriesHasMore, setCategoriesHasMore] = useState(true);
  const [categoriesLoadingMore, setCategoriesLoadingMore] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [connectAccountModalOpen, setConnectAccountModalOpen] = useState(false);
  const [userPoints, setUserPoints] = useState(0);
  const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);
  const [redeemModalRestaurant, setRedeemModalRestaurant] = useState(null);
  const [gamesData, setGamesData] = useState([]);

  const handleNavigateToWallet = () => {
    navigate("/wallet");
  };

  // Redeem modal handlers
  const handleOpenRedeemModal = () => {
    setRedeemModalRestaurant(null);
    setIsRedeemModalOpen(true);
  };

  const handleRedeemRestaurant = (restaurant) => {
    setRedeemModalRestaurant({
      id: restaurant.id,
      name: restaurant.name,
      image: restaurant.image,
      category: restaurant.cuisine || restaurant.category,
      location: restaurant.distance || "Nearby",
    });
    setIsRedeemModalOpen(true);
  };

  const handleSelectRestaurant = (restaurantId) => {
    // Navigate to restaurant detail page or open modal
    // For now, this is a placeholder
    console.log("Select restaurant:", restaurantId);
  };

  const handleOpenFilters = () => {
    // Open filters panel
    // For now, this is a placeholder
    console.log("Open filters");
  };

  const handleToggleLike = (restaurantId, isLiked) => {
    setRestaurants((prev) =>
      prev.map((r) => (r.id === restaurantId ? { ...r, liked: isLiked } : r)),
    );
  };

  const handleRatingChanged = () => {
    // Reload restaurants to get updated average ratings
    fetchRestaurants(1);
  };

  const handleCloseRedeemModal = () => {
    setIsRedeemModalOpen(false);
    setRedeemModalRestaurant(null);
  };

  // Initialize connected account from localStorage
  const getInitialConnectedAccount = () => {
    try {
      const stored = localStorage.getItem("connectedAccount");
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error("Failed to parse stored account data:", e);
    }
    return {
      connected: false,
      accountNumber: null,
      cashbackPercent: 5,
    };
  };

  const [connectedAccount, setConnectedAccount] = useState(
    getInitialConnectedAccount(),
  );

  // Fallback location for distance calculation when user location is not available
  const fallbackLat = 14.4583647;
  const fallbackLng = 100.131481;

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Calculate distance between two coordinates using Haversine formula (returns miles)
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;

    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) *
        Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance.toFixed(1);
  }, []);

  // Debounce search value (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);

  // Fetch categories from API
  const fetchCategories = useCallback(async (page = 1, append = false) => {
    try {
      const response = await get(`${endpoints.categories}?page=${page}`);
      const apiCategories = response.data.data?.data || [];
      const pagination = response.data.data;

      // Transform API categories to component format
      const transformedCategories = apiCategories.map((cat) => ({
        id: cat.id.toString(),
        label: cat.title,
        icon: cat.icon || cat.avatar_icon,
      }));

      if (append) {
        // Append new categories to existing list
        setCategories((prev) => [...prev, ...transformedCategories]);
      } else {
        // Set initial categories with "All" at the beginning
        setCategories([
          { id: "all", label: "All", icon: "mdi-view-grid-outline" },
          ...transformedCategories,
        ]);
      }

      // Update pagination state
      setCategoriesCurrentPage(pagination?.current_page || page);
      setCategoriesHasMore(pagination?.current_page < pagination?.last_page);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
      // Use default categories as fallback
      if (!append) {
        setCategories([
          { id: "all", label: "All", icon: "mdi-view-grid-outline" },
        ]);
      }
    }
  }, []);

  // Load more categories (pagination)
  const loadMoreCategories = useCallback(async () => {
    if (categoriesLoadingMore || !categoriesHasMore) return;

    setCategoriesLoadingMore(true);
    const nextPage = categoriesCurrentPage + 1;
    await fetchCategories(nextPage, true);
    setCategoriesLoadingMore(false);
  }, [
    categoriesLoadingMore,
    categoriesHasMore,
    categoriesCurrentPage,
    fetchCategories,
  ]);

  // Fetch connected accounts from API
  const fetchConnectedAccounts = useCallback(async () => {
    try {
      const response = await get(endpoints.financialConnectionsAccounts);
      const accounts = response.data.data || [];

      if (accounts.length > 0) {
        // Get the first connected account
        const account = accounts[0];
        // Format account number using last4 field
        const accountNumber = account.last4
          ? `********${account.last4}`
          : account.display_name
            ? account.display_name
            : "Connected";

        const accountData = {
          connected: true,
          accountNumber: accountNumber,
          cashbackPercent: 5,
        };

        setConnectedAccount(accountData);
        // Persist to localStorage
        localStorage.setItem("connectedAccount", JSON.stringify(accountData));
      } else {
        const accountData = {
          connected: false,
          accountNumber: null,
          cashbackPercent: 5,
        };
        setConnectedAccount(accountData);
        localStorage.setItem("connectedAccount", JSON.stringify(accountData));
      }
    } catch (err) {
      console.error("Failed to fetch connected accounts:", err);
      const accountData = {
        connected: false,
        accountNumber: null,
        cashbackPercent: 5,
      };
      setConnectedAccount(accountData);
      localStorage.setItem("connectedAccount", JSON.stringify(accountData));
    }
  }, []);

  // Fetch points from API
  const fetchPoints = useCallback(async () => {
    try {
      const response = await get(endpoints.pointsBalance);
      const apiPoints = response.data.data?.points || 0;
      setUserPoints(apiPoints);
    } catch (err) {
      console.error("Failed to fetch points:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
      // Use 0 as fallback
      setUserPoints(0);
    }
  }, []);

  // Fetch points on mount
  useEffect(() => {
    fetchPoints();
  }, [fetchPoints]);

  // Fetch games from API
  const fetchGames = useCallback(async () => {
    try {
      const response = await get(endpoints.games);
      if (response.data.success) {
        setGamesData(response.data.data.games || []);
      }
    } catch (err) {
      console.error("Failed to fetch games:", err);
      setGamesData([]);
    }
  }, []);

  // Fetch games on mount
  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  // Transform games data for mini-widget
  const getStreakGameData = useCallback(() => {
    const streakGame = gamesData.find((game) => game.type === "streak");
    if (streakGame) {
      const progress = streakGame.progress;
      const weeklyCompletion = progress.weekly_completion || [];
      const completedWeeks = weeklyCompletion.filter((w) => w.completed).length;
      const result = {
        name: streakGame.name,
        description: streakGame.description,
        weeksCompleted: completedWeeks,
        totalWeeks: weeklyCompletion.length || 4,
        pointsReward: progress.reward_points || 1000,
        weeklyCompletion: weeklyCompletion.map((w) => ({
          week: w.week,
          completed: w.completed,
          start: w.start,
        })),
      };
      return result;
    }
    // Return empty object when no streak game exists
    return {
      name: null,
      description: null,
      weeksCompleted: 0,
      totalWeeks: 0,
      pointsReward: 0,
      weeklyCompletion: [],
    };
  }, [gamesData]);

  const getVisitGameData = useCallback(() => {
    const visitGame = gamesData.find(
      (game) =>
        (game.type === "visit" || game.type === "visits") &&
        game.progress?.status === "active",
    );
    if (visitGame) {
      const progress = visitGame.progress;
      const result = {
        name: visitGame.name,
        description: visitGame.description,
        visited: progress.visited_restaurants?.length || 0,
        total: progress.target_value || 200,
        pointsReward: progress.reward_points || 50000,
      };
      return result;
    }
    // Return empty object when no active visit game
    return {
      name: null,
      description: null,
      visited: 0,
      total: 0,
      pointsReward: 0,
    };
  }, [gamesData]);

  const rewardsData = useMemo(
    () => ({
      accountState: connectedAccount || {
        connected: false,
        cashbackPercent: 5,
        accountNumber: null,
      },
      diningStreak: getStreakGameData(),
      passportChallenge: getVisitGameData(),
      rewardPoints: {
        points: userPoints,
        nextRewardValue: 50,
        nextRewardPoints: 6000,
      },
    }),
    [connectedAccount, userPoints, getStreakGameData, getVisitGameData],
  );

  // Dummy restaurant data for fallback

  // Transform API data to component format
  const transformRestaurantData = useCallback(
    (apiRestaurant) => {
      const categories = apiRestaurant.categories || [];
      const locationCategories = apiRestaurant.location_categories || [];
      const allCategories =
        categories.length > 0 ? categories : locationCategories;
      const firstCategory = allCategories[0];

      const isValidImageUrl = (url) => {
        if (!url) return false;
        if (
          url.includes("encrypted-tbn0.gstatic.com") &&
          url.includes("q=tbn:")
        ) {
          return false;
        }
        return true;
      };

      const bannerImage = isValidImageUrl(apiRestaurant.banner_url)
        ? apiRestaurant.banner_url
        : fallbackImage;

      const restaurantLat =
        apiRestaurant.latitude || apiRestaurant.address?.latitude;
      const restaurantLng =
        apiRestaurant.longitude || apiRestaurant.address?.longitude;
      const locationToUse = userLocation || {
        lat: fallbackLat,
        lng: fallbackLng,
      };
      const distance =
        restaurantLat && restaurantLng
          ? `${calculateDistance(locationToUse.lat, locationToUse.lng, restaurantLat, restaurantLng)} mi`
          : null;

      return {
        id: apiRestaurant.id,
        name: apiRestaurant.title,
        image: bannerImage,
        badge: apiRestaurant.premium
          ? { label: "Premium", variant: "popular" }
          : null,
        rating: apiRestaurant.resturant_rating?.rating || 0,
        cuisine: firstCategory?.title || "Restaurant",
        categories: allCategories.map((cat) => cat.title),
        distance,
        category: firstCategory?.slug || "all",
        liked: apiRestaurant.is_wishlist || false,
        is_visited: apiRestaurant.is_visited || 0,
        latitude: restaurantLat,
        longitude: restaurantLng,
      };
    },
    [userLocation, calculateDistance],
  );

  // Fetch restaurant list
  const fetchRestaurants = useCallback(
    async (page = 1, append = false) => {
      if (!append) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        // Build query parameters
        const params = new URLSearchParams({
          is_member: "1",
          page_size: "8",
          page: page.toString(),
        });

        // Add sort_by only if it's not empty
        if (sortBy) {
          params.append("sort_by", sortBy);
        }

        // Add category_id if not "all"
        if (activeCategory !== "all") {
          params.append("category_id", activeCategory);
        }

        // Add search_by if search value exists
        if (debouncedSearchValue.trim()) {
          params.append("search_by", debouncedSearchValue.trim());
        }

        // Add is_visited filter if not "all"
        if (visitedFilter === "visited") {
          params.append("is_visited", "1");
        } else if (visitedFilter === "not_visited") {
          params.append("is_visited", "0");
        }

        const response = await get(
          `${endpoints.restaurantList}?${params.toString()}`,
        );
        const transformedData = response.data.data.map(transformRestaurantData);

        // Append or replace based on the append flag
        if (append) {
          setRestaurants((prev) => {
            // Deduplicate restaurants by ID to prevent duplicates
            const existingIds = new Set(prev.map((r) => r.id));
            const newRestaurants = transformedData.filter(
              (r) => !existingIds.has(r.id),
            );

            // Only append if we have new items
            if (newRestaurants.length === 0) {
              return prev;
            }

            return [...prev, ...newRestaurants];
          });
        } else {
          setRestaurants(transformedData);
        }

        // Check if there are more pages
        const lastPage = response.data.last_page;
        setCurrentPage(response.data.current_page);
        setHasMore(response.data.current_page < lastPage);

        // Set total count from API response
        if (response.data.total !== undefined) {
          setTotalRestaurants(response.data.total);
        }
      } catch (err) {
        const errorMessage = extractErrorMessage(err);
        toastr.error(errorMessage);
      } finally {
        if (!append) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    },
    [
      transformRestaurantData,
      activeCategory,
      debouncedSearchValue,
      sortBy,
      visitedFilter,
    ],
  );

  // Viewport detection for large screens
  const scrollContainerRef = React.useRef(null);

  useEffect(() => {
    const checkIfFitsInViewport = () => {
      if (scrollContainerRef.current) {
        const { scrollHeight, clientHeight } = scrollContainerRef.current;
        setShowLoadMoreButton(
          scrollHeight <= clientHeight && hasMore && !loadingMore,
        );
      }
    };

    checkIfFitsInViewport();

    const resizeObserver = new ResizeObserver(checkIfFitsInViewport);
    if (scrollContainerRef.current) {
      resizeObserver.observe(scrollContainerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [restaurants, hasMore, loadingMore]);

  // Fetch categories once on mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Fetch connected accounts once on mount
  useEffect(() => {
    fetchConnectedAccounts();
  }, [fetchConnectedAccounts]);

  // Check for stored location permission and request if needed
  useEffect(() => {
    const storedLocation = localStorage.getItem("userLocation");
    if (storedLocation) {
      setUserLocation(JSON.parse(storedLocation));
    } else {
      setShowLocationModal(true);
    }
  }, []);

  const handleAllowLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserLocation(location);
          localStorage.setItem("userLocation", JSON.stringify(location));
          setShowLocationModal(false);
        },
        (error) => {
          console.error("Error getting location:", error);
          toastr.error(
            "Unable to get your location. Distance will not be calculated.",
          );
          setShowLocationModal(false);
        },
      );
    } else {
      toastr.error("Geolocation is not supported by your browser.");
      setShowLocationModal(false);
    }
  };

  const handleDenyLocation = () => {
    setShowLocationModal(false);
  };

  // Fetch restaurants when filters change
  useEffect(() => {
    fetchRestaurants(1);
  }, [fetchRestaurants]);

  // Load more restaurants (infinite scroll)
  const loadMoreRestaurants = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    await fetchRestaurants(nextPage, true);
  }, [loadingMore, hasMore, currentPage, fetchRestaurants]);

  // Filter change handlers
  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setCurrentPage(1);
    setHasMore(true);
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setCurrentPage(1);
    setHasMore(true);
  };

  const handleSortChange = (sortValue) => {
    setSortBy(sortValue);
    setCurrentPage(1);
    setHasMore(true);
  };

  const handleVisitedFilterChange = (filterValue) => {
    setVisitedFilter(filterValue);
    setCurrentPage(1);
    setHasMore(true);
  };

  const handleSettings = () => {
    navigate("/settings");
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {!connectedAccount.connected && (
            <CardConnectBanner
              onConnect={() => setConnectAccountModalOpen(true)}
              onLearnMore={() => setConnectAccountModalOpen(true)}
            />
          )}
          <Row>
            <TopRewardsWidget
              data={rewardsData}
              onConnectAccount={() => setConnectAccountModalOpen(true)}
              onSettings={handleSettings}
              onViewStreak={() => navigate("/games")}
              onViewChallenge={() => navigate("/games")}
              onRedeemPoints={handleOpenRedeemModal}
            />
          </Row>
          <ExplorePartnersSection
            restaurants={restaurants}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            totalRestaurants={totalRestaurants}
            onLoadMore={loadMoreRestaurants}
            onSelectRestaurant={handleSelectRestaurant}
            onRedeemRestaurant={handleRedeemRestaurant}
            onToggleLike={handleToggleLike}
            onRatingChanged={handleRatingChanged}
            onOpenFilters={handleOpenFilters}
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange}
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
            sortBy={sortBy}
            onSortChange={handleSortChange}
            categories={categories}
            categoriesHasMore={categoriesHasMore}
            categoriesLoadingMore={categoriesLoadingMore}
            onLoadMoreCategories={loadMoreCategories}
            visitedFilter={visitedFilter}
            onVisitedFilterChange={handleVisitedFilterChange}
          />
          {/* Load More button for large screens where content fits in viewport */}
          {showLoadMoreButton && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <SecondaryButton
                onClick={loadMoreRestaurants}
                loading={loadingMore}
              >
                Load More Restaurants
              </SecondaryButton>
            </div>
          )}
        </Container>
      </div>

      {/* Location Permission Modal */}
      {showLocationModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "24px",
              borderRadius: "12px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            }}
          >
            <h3
              style={{
                marginBottom: "12px",
                fontSize: "20px",
                fontWeight: 600,
              }}
            >
              Enable Location
            </h3>
            <p
              style={{ marginBottom: "24px", color: "#666", lineHeight: "1.5" }}
            >
              Allow us to access your location to show you the distance to each
              restaurant.
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <SecondaryButton
                label="Not Now"
                variant="light-grey"
                onClick={handleDenyLocation}
              />
              <SecondaryButton
                label="Allow Location"
                variant="orange"
                onClick={handleAllowLocation}
              />
            </div>
          </div>
        </div>
      )}

      {/* Connect Account Modal */}
      <ConnectAccountModal
        isOpen={connectAccountModalOpen}
        onClose={() => setConnectAccountModalOpen(false)}
        onConnect={() => {
          setConnectAccountModalOpen(false);
          fetchConnectedAccounts();
        }}
        onCancel={() => setConnectAccountModalOpen(false)}
      />

      {/* Redeem Voucher Modal */}
      <RedeemVoucherModal
        isOpen={isRedeemModalOpen}
        onClose={handleCloseRedeemModal}
        userPoints={userPoints}
        onVoucherCreated={() => {
          // Refresh points after voucher creation
          fetchPoints();
        }}
        onNavigateToWallet={handleNavigateToWallet}
        preSelectedRestaurant={redeemModalRestaurant}
      />
    </React.Fragment>
  );
};

export default Dashboard;
